/**
 * The API client, with single-flight token refresh (P0-TR4, D-011).
 *
 * When a partner opens the dashboard, several queries fire at once. Their
 * access token expires after 15 minutes, so all of them can come back 401
 * together. A naive interceptor would then send several refresh requests —
 * and because refresh tokens rotate and are single-use, the second one would
 * present an already-exchanged token, which the server correctly reads as
 * theft and responds to by revoking the whole family (D-011).
 *
 * In other words: without single-flighting, simply loading the dashboard on a
 * slow connection would log the partner out. So concurrent 401s share one
 * in-flight refresh and every waiting request retries with its result.
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { getAccessToken, notifyUnauthorized, setAccessToken } from './tokenStore.js';

interface RetriableConfig extends InternalAxiosRequestConfig {
  /** Set once a request has been retried, so a failure cannot loop. */
  _retriedAfterRefresh?: boolean;
  /**
   * Marks the refresh call itself. Without it, a 401 from `/auth/refresh`
   * would re-enter the interceptor and try to refresh again — an infinite
   * loop at exactly the moment the session has ended.
   */
  _isRefreshRequest?: boolean;
}

export const API_BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // Sends the httpOnly refresh cookie on the auth routes it is scoped to.
  withCredentials: true,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** The one refresh in flight, if any. This is what makes it single-flight. */
let inFlightRefresh: Promise<string> | null = null;

async function requestNewAccessToken(): Promise<string> {
  const response = await api.post<{ success: true; data: { accessToken: string } }>('/auth/refresh', undefined, {
    _isRefreshRequest: true,
  } as AxiosRequestConfig);

  const token = response.data.data.accessToken;
  setAccessToken(token);
  return token;
}

/**
 * Returns the in-flight refresh if there is one, otherwise starts it.
 *
 * The promise is cleared in `finally` so a later expiry can refresh again — and
 * so a failed refresh does not leave every future request waiting on a promise
 * that already rejected.
 */
export function refreshAccessToken(): Promise<string> {
  inFlightRefresh ??= requestNewAccessToken().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;

    // Only a 401 is refreshable. A 403 means the partner is authenticated and
    // simply not allowed, and refreshing would not change that. The refresh
    // call itself is excluded, or a dead session would loop forever.
    if (error.response?.status !== 401 || !config || config._retriedAfterRefresh || config._isRefreshRequest === true) {
      return Promise.reject(error);
    }

    config._retriedAfterRefresh = true;

    try {
      const token = await refreshAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
      return await api.request(config as AxiosRequestConfig);
    } catch {
      // The refresh cookie is missing, expired or revoked. The session is over;
      // AuthContext listens for this and sends the partner to login.
      notifyUnauthorized();
      return Promise.reject(error);
    }
  },
);

/** Test-only: clears any in-flight refresh between cases. */
export function resetRefreshState(): void {
  inFlightRefresh = null;
}
